import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './Verification.css';

const Verification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const rental = location.state?.rental;
  const serviceLogoUrl = location.state?.serviceLogoUrl;
  const countryFlagUrl = location.state?.countryFlagUrl || rental?.data?.rental?.countryFlagUrl;
  const countryName = location.state?.countryName;

  const isLongTerm = !!rental?.data?.rental;
  const [rentalData, setRentalData] = useState(rental?.data?.userRentalData || rental?.data?.rental || null);
  const [messages, setMessages] = useState(rental?.data?.messages || []);
  const [userBalance, setUserBalance] = useState(rental?.data?.userBalance || null);
  const [canceling, setCanceling] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const pollRef = useRef(null);

  // Calculate time left
  useEffect(() => {
    if (!rentalData?.availableUntil && !rentalData?.finishedAt && !rentalData?.expiresAt) return;

    const updateTimer = () => {
      let remaining;
      if (rentalData.expiresAt) {
        // Long-term rental
        remaining = new Date(rentalData.expiresAt).getTime() - Date.now();
      } else if (rentalData.availableUntil) {
        remaining = rentalData.availableUntil - (Date.now() - new Date(rental?.timestamp || Date.now()).getTime());
      } else {
        remaining = new Date(rentalData.finishedAt).getTime() - Date.now();
      }
      setTimeLeft(Math.max(0, remaining));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [rentalData]);

  // Poll for SMS messages
  const pollMessages = useCallback(async () => {
    if (!rentalData?.phoneNumber) return;
    try {
      const endpoint = isLongTerm
        ? `/v1/users/long-term-rentals/${rentalData.phoneNumber}/sms`
        : `/v1/users/rentals/${rentalData.phoneNumber}/sms`;
      const response = await api.get(endpoint, true);
      if (response?.data) {
        if (response.data.messages) setMessages(response.data.messages);
        if (response.data.userRentalData) setRentalData(response.data.userRentalData);
        if (response.data.rental) setRentalData(response.data.rental);
        if (response.data.userBalance) setUserBalance(response.data.userBalance);
      }
    } catch (err) {
      console.error('Failed to poll messages:', err);
    }
  }, [rentalData?.phoneNumber, isLongTerm]);

  useEffect(() => {
    if (!rentalData?.phoneNumber) return;
    pollRef.current = setInterval(pollMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [rentalData?.phoneNumber, pollMessages]);

  const handleCancel = async () => {
    if (!rentalData?.phoneNumber || canceling) return;
    setCanceling(true);
    try {
      const endpoint = isLongTerm
        ? `/v1/users/long-term-rentals/cancel/${rentalData._id}`
        : `/v1/users/rentals/cancel/${rentalData._id}`;
      const response = await api.post(endpoint, null, true);
      if (response?.data?.success) {
        clearInterval(pollRef.current);
        navigate(isLongTerm ? '/long-term' : '/services');
      }
    } catch (err) {
      console.error('Failed to cancel rental:', err);
    } finally {
      setCanceling(false);
    }
  };

  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(rentalData.phoneNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const str = String(phone);
    // Format as groups: +X XXX XXX XX XX or similar
    return str.replace(/(\d{1,3})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 $2 $3 $4 $5');
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!rental || !rentalData) {
    return (
      <div className="verification-page">
        <div className="container">
          <div className="empty-state">
            <span className="material-symbols-outlined empty-icon">error</span>
            <p className="empty-title">No active rental</p>
            <p className="empty-description">Please rent a number first.</p>
            <button className="back-btn" onClick={() => navigate('/services')}>
              Go to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isActive = timeLeft > 0;

  return (
    <div className="verification-page">
      <div className="container">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <a href="/">Home</a>
          <span className="material-symbols-outlined">chevron_right</span>
          <a href="/services">Services</a>
          <span className="material-symbols-outlined">chevron_right</span>
          <span className="breadcrumb-current">Verification</span>
        </div>

        {/* Header */}
        <div className="verification-header">
          <h1 className="page-title">Get Verification Code</h1>
          <div className="service-info">
            {serviceLogoUrl && (
              <div className="service-badge">
                <img src={serviceLogoUrl} alt={rentalData.serviceName} className="service-icon" />
                <span>{rentalData.serviceName?.toUpperCase()}</span>
              </div>
            )}
            {!serviceLogoUrl && rentalData.serviceName && (
              <div className="service-badge">
                <span className="material-symbols-outlined service-icon-fallback">apps</span>
                <span>{rentalData.serviceName?.toUpperCase()}</span>
              </div>
            )}
            {countryFlagUrl && (
              <>
                <span className="separator">|</span>
                <div className="country-badge">
                  <img src={countryFlagUrl} alt={countryName} className="country-flag" />
                  <span>{countryName}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Phone Number Card */}
        <div className="rental-card">
          <div className="rental-card-header">
            <div className="rental-phone-section">
              <span className="rental-label">Your Number</span>
              <div className="rental-phone-row">
                {countryFlagUrl && (
                  <img src={countryFlagUrl} alt="" className="phone-country-flag" />
                )}
                <p className="rental-phone">{formatPhoneNumber(rentalData.phoneNumber)}</p>
                <button
                  className={`copy-btn${copied ? ' copied' : ''}`}
                  onClick={handleCopyPhone}
                  title="Copy number"
                >
                  <span className="material-symbols-outlined">
                    {copied ? 'check' : 'content_copy'}
                  </span>
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>
            <div className="rental-status-section">
              {isActive ? (
                <div className="timer-badge active">
                  <span className="material-symbols-outlined">timer</span>
                  <span>{formatTime(timeLeft)}</span>
                </div>
              ) : (
                <div className="timer-badge expired">
                  <span className="material-symbols-outlined">timer_off</span>
                  <span>Expired</span>
                </div>
              )}
              {isActive && (
                <button
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={canceling}
                >
                  {canceling ? (
                    <>
                      <span className="btn-spinner"></span>
                      <span>Canceling...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">close</span>
                      <span>Cancel Number</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* SMS Inbox */}
        <div className="inbox-card">
          <div className="inbox-header">
            <h3 className="inbox-title">Incoming Messages</h3>
            <div className="inbox-actions">
              <button className="icon-btn" onClick={pollMessages} title="Refresh">
                <span className="material-symbols-outlined">refresh</span>
              </button>
            </div>
          </div>
          <div className="inbox-content">
            {messages.length === 0 ? (
              <div className="empty-state">
                <span className="material-symbols-outlined empty-icon">schedule</span>
                <p className="empty-title">Waiting for new SMS...</p>
                <p className="empty-description">Messages will appear here automatically.</p>
              </div>
            ) : (
              <div className="messages-list">
                {messages.map((msg, index) => (
                  <div key={index} className="message-item">
                    <div className="message-content">
                      <p className="message-text">{msg.text || msg.message}</p>
                      <span className="message-time">
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <button
                      className="icon-btn"
                      onClick={() => handleCopy(msg.text || msg.message)}
                      title="Copy message"
                    >
                      <span className="material-symbols-outlined">content_copy</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Verification;
